import React, { useEffect, useMemo, useState } from "react";

import {
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Table,
  Space,
  InputNumber,
  Typography,
  Tag,
  Statistic,
  Grid,
  Alert,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import {
  GoogleMap,
  useJsApiLoader,
  DirectionsRenderer,
  Marker,
  Polyline,
} from "@react-google-maps/api";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import { useDispatch, useSelector } from "react-redux";

import {
  fetchWorkRoutes,
  saveWorkRoute,
  deleteWorkRoute,
} from "../workRoutesThunk";
import { GOOGLE_MAP_LIBRARIES } from "../../../constants/googleMaps";
import {
  generateAutoRoutesApi,
  getRouteActiveExecutionsApi,
  previewAutoRoutesApi,
} from "../../../apiservice/hrpayroll";

const { Text } = Typography;
const { useBreakpoint } = Grid;



const libraries = GOOGLE_MAP_LIBRARIES;

const ROUTE_COLORS = [
  "#1890ff",
  "#ff4d4f",
  "#52c41a",
  "#fa8c16",
  "#722ed1",
  "#13c2c2",
  "#eb2f96",
];

const Routes = ({ center, zoom }) => {
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Enhanced for robust scaling on varying screens
  const containerStyle = {
    width: "100%",
    height: isMobile ? "50vh" : "100vh",
    minHeight: "350px",
    maxHeight: "calc(100vh - 180px)",
  };


  const { workRoutes, routePoints, routeAreas } = useSelector(
    (state) => state.workRoutes,
  );

  const defaultCenter = center || {
    lat: 25.2048,
    lng: 55.2708,
  };

  const mapApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: mapApiKey,
    libraries,
  });

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [directions, setDirections] = useState(null);
  const [routeMode, setRouteMode] = useState("manual");
  const [remainderStrategy, setRemainderStrategy] = useState("separate");
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [optimizationStrategy, setOptimizationStrategy] = useState("nearest");
  const [pointToAdd, setPointToAdd] = useState();
  const [searchText, setSearchText] = useState("");

  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchWorkRoutes());
  }, [dispatch]);  

  useEffect(() => {
    if (!window.google || selectedPoints.length < 2) {
      setDirections(null);
      return;
    }
    

    const directionsService = new window.google.maps.DirectionsService();

    const origin = {
      lat: Number(selectedPoints[0].latitude),
      lng: Number(selectedPoints[0].longitude),
    };

    const destination = {
      lat: Number(selectedPoints[selectedPoints.length - 1].latitude),
      lng: Number(selectedPoints[selectedPoints.length - 1].longitude),
    };

    const waypoints = selectedPoints
      .slice(1, selectedPoints.length - 1)
      .map((p) => ({
        location: {
          lat: Number(p.latitude),
          lng: Number(p.longitude),
        },
        stopover: true,
      }));

    directionsService.route(
      {
        origin,
        destination,
        waypoints,
        optimizeWaypoints: false,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);
        }
      },
    );
  }, [selectedPoints]);

  const filteredRoutes = useMemo(() => {
    if (!searchText) return workRoutes;

    const search = searchText.toLowerCase();

    return workRoutes.filter((route) => {
      const pointNames =
        route.points?.map((p) => p.name).join(" ") || "";

      return (
        route.name?.toLowerCase().includes(search) ||
        route.description?.toLowerCase().includes(search) ||
        pointNames.toLowerCase().includes(search)
      );
    });
  }, [workRoutes, searchText]);

  const previewCenter = useMemo(() => {
    if (!previewData?.routes?.length) return defaultCenter;

    const firstPoint = previewData.routes?.[0]?.points?.[0];

    if (!firstPoint) return defaultCenter;

    return {
      lat: Number(firstPoint.latitude),
      lng: Number(firstPoint.longitude),
    };
  }, [previewData, defaultCenter]);

  const openAddModal = () => {
    form.resetFields();
    setSelectedRoute(null);
    setSelectedPoints([]);
    setDirections(null);
    setPreviewData(null);
    setRouteMode("manual");
    setPointToAdd(undefined);
    setOpen(true);
  };

  const openEditModal = async (route) => {
    const res = await getRouteActiveExecutionsApi({ route_id : route.id });
    setSelectedRoute({
      ...route,
      active_execution_count: res?.data?.active_execution_count || 0,
    });
    form.setFieldsValue({
      name: route.name,
      description: route.description,
      is_ordered: !!route.is_ordered,      
    });
    setSelectedPoints(route.points || []);
    setOpen(true);
  };

  const handleAddPoint = (pointId) => {
    const point = routePoints.find((p) => p.id === pointId);
    if (!point) return;
    setSelectedPoints((prev) => [
      ...prev,
      {
        ...point,
        route_point_id: point.id,
        expected_duration_minutes: 15,
        is_mandatory: true,
      },
    ]);
  };

  const handleSave = async () => {
    // Guards against a double-click or a slow-network retry firing this
    // twice concurrently -- the backend save isn't safe to run twice in
    // parallel (it rebuilds the route's points from scratch each time).
    if (saving) return;

    let values;
     try {
     values = await form.validateFields();
      } catch {
      return;
    }

    setSaving(true);
    try {

    if (routeMode !== "auto") {
      if (!selectedPoints.length) {
        return Modal.warning({
          title: "Route Point Required",
          content:
            "Please add at least one route point before creating the route.",
        });
      }

      const duplicatePoints = new Set();

      for (const point of selectedPoints) {
        if (duplicatePoints.has(point.route_point_id)) {
          Modal.warning({
            title: "Duplicate Route Point",
            content: "A route point can only be added once in a route.",
          });
          return;
        }

        duplicatePoints.add(point.route_point_id);
      }
    }

    if (routeMode === "auto") {
      try {
        await generateAutoRoutesApi({
          area_id: values.auto_area,
          points_per_route: values.points_per_route,
          remainder_strategy: remainderStrategy,
          route_prefix: values.route_prefix,
          optimization_strategy: optimizationStrategy,
        });
      } catch (error) {
        if (error?.response?.data?.code === "AREA_ALREADY_GENERATED") {
          return Modal.confirm({
            title: "Routes already exist",
            content: "Do you want to regenerate routes for this area?",
            onOk: async () => {
              await generateAutoRoutesApi({
                area_id: values.auto_area,
                points_per_route: values.points_per_route,
                remainder_strategy: remainderStrategy,
                route_prefix: values.route_prefix,
                optimization_strategy: optimizationStrategy,
                force_generate: true,
                regenerate: true,
              });
              dispatch(fetchWorkRoutes());
              setOpen(false);
            },
          });
        } else {
          throw error;
        }
      }

      dispatch(fetchWorkRoutes());
      setOpen(false);
      return;
    }
        try {
      await dispatch(
        saveWorkRoute({
          id: selectedRoute?.id,
          ...values,
          points: selectedPoints,
        }),
      ).unwrap();

      dispatch(fetchWorkRoutes());

      setOpen(false);

    } catch (err) {

      if (err?.code === "ACTIVE_ROUTE_EXECUTION") {
        Modal.warning({
          title: "Route Execution In Progress",
          content: err.message,
        });

        return;
      }

      Modal.error({
        title: "Save Failed",
        content:
          err?.message ||
          "Unable to save route.",
      });
    }
    } finally {
      setSaving(false);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(selectedPoints);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setSelectedPoints(items);
  };

  const handlePreviewRoutes = async () => {
    try {
      setPreviewLoading(true);
      const values = await form.validateFields([
        "auto_area",
        "points_per_route",
      ]);
      const res = await previewAutoRoutesApi({
        area_id: values.auto_area,
        points_per_route: values.points_per_route,
        remainder_strategy: remainderStrategy,
        optimization_strategy: optimizationStrategy,
      });

      setPreviewData(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Ordered",
      dataIndex: "is_ordered",
      render: (v) => (v ? "Yes" : "No"),
    },
    {
      title: "Points",
      render: (_, record) => record.points?.length || 0,
    },
    {
      title: "Action",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined/>} onClick={() => openEditModal(record)}/>
          <Button
            danger
            icon={<DeleteOutlined/>}
            onClick={() =>
              dispatch(
                deleteWorkRoute({
                  id: record.id,
                }),
              )
            }
          />          
        </Space>
      ),
    },
  ];

  const areaOptions = routeAreas.map((area) => ({
    label: area.name,
    value: area.id,
  }));

  return (
    <div style={{ padding: "8px" }}>
      {/* Main View Grid Structure */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card
            title={`Routes (${filteredRoutes.length}/${workRoutes.length})`}
            extra={
              <Button type="primary" onClick={openAddModal}>
                Add Route
              </Button>
            }
          >
            <Input.Search
              placeholder="Search routes..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ marginBottom: 12 }}
            />

            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredRoutes}
              pagination={false}
              scroll={{ x: "max-content" }}
              size="small"
            />
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={defaultCenter}
              zoom={zoom}
            >
              {directions && <DirectionsRenderer directions={directions} />}

              {selectedPoints.map((point) => (
                <Marker
                  key={point.id}
                  position={{
                    lat: Number(point.latitude),
                    lng: Number(point.longitude),
                  }}
                />
              ))}
            </GoogleMap>
          )}
        </Col>
      </Row>

      {/* Editor & Generator Workspace Modal */}
      <Modal
        open={open}
        width={1200}
        style={{ top: 20 }}
        title={selectedRoute ? "Edit Route" : "Create Route"}
        onCancel={() => {
          setPointToAdd(undefined);
          setOpen(false);
        }}
        onOk={handleSave}
        confirmLoading={saving}
        okText={
          routeMode === "auto"
            ? previewData
              ? `Generate ${previewData.total_routes} Routes`
              : "Generate Routes"
            : selectedRoute
              ? "Update Route"
              : "Create Route"
        }
      >
        <Row gutter={[16, 16]}>
          {/* Form & Sequence Panel */}
          <Col
            xs={24}
            md={routeMode === "auto" ? 24 : 10}
            lg={routeMode === "auto" ? 24 : 8}
          >
            <Form layout="vertical" form={form}>
              <Form.Item label="Route Creation Mode">
                <Select
                  value={routeMode}
                  onChange={setRouteMode}
                  options={[
                    { label: "Manual", value: "manual" },
                    { label: "Automatic", value: "auto" },
                  ]}
                />
              </Form.Item>

              {/* Automatic Parameter Configuration Section */}
              {routeMode === "auto" && (
                <>
                  {previewData && (
                    <Card
                      size="small"
                      title="Generated Routes Preview"
                      style={{ marginTop: 16, marginBottom: 16 }}
                    >
                      {isLoaded && previewData?.routes?.length > 0 && (
                        <Card>
                          <GoogleMap
                            mapContainerStyle={{
                              width: "100%",
                              height: "400px",
                            }}
                            center={previewCenter}
                            zoom={11}
                          >
                            {previewData.routes.map((route, routeIndex) => {
                              const color =
                                ROUTE_COLORS[routeIndex % ROUTE_COLORS.length];
                              const path = route.points.map((point) => ({
                                lat: Number(point.latitude),
                                lng: Number(point.longitude),
                              }));

                              return (
                                <React.Fragment key={route.name}>
                                  {route.points.map((point, pointIndex) => (
                                    <Marker
                                      key={point.id}
                                      position={{
                                        lat: Number(point.latitude),
                                        lng: Number(point.longitude),
                                      }}
                                      label={`${pointIndex + 1}`}
                                    />
                                  ))}

                                  <Polyline
                                    path={path}
                                    options={{
                                      strokeColor: color,
                                      strokeOpacity: 0.9,
                                      strokeWeight: 4,
                                    }}
                                  />
                                </React.Fragment>
                              );
                            })}
                          </GoogleMap>
                        </Card>
                      )}

                      <div style={{ marginTop: 16 }}>
                        <Space wrap>
                          {previewData.routes.map((route, index) => (
                            <Tag
                              key={route.name}
                              color={ROUTE_COLORS[index % ROUTE_COLORS.length]}
                            >
                              {route.name}
                            </Tag>
                          ))}
                        </Space>
                      </div>

                      <Row
                        gutter={12}
                        style={{ marginTop: 16, marginBottom: 16 }}
                      >
                        <Col span={12}>
                          <Card size="small">
                            <Statistic
                              title="Total Points"
                              value={previewData.total_points}
                            />
                          </Card>
                        </Col>
                        <Col span={12}>
                          <Card size="small">
                            <Statistic
                              title="Total Routes"
                              value={previewData.total_routes}
                            />
                          </Card>
                        </Col>
                      </Row>

                      <Table
                        size="small"
                        pagination={false}
                        rowKey="name"
                        dataSource={previewData.routes}
                        columns={[
                          { title: "Route Name", dataIndex: "name" },
                          { title: "Points", dataIndex: "point_count" },
                        ]}
                        scroll={{ x: "max-content" }}
                      />
                    </Card>
                  )}

                  <Form.Item
                    label="Area"
                    name="auto_area"
                    rules={[{ required: true, message: "Select area" }]}
                  >
                    <Select showSearch options={areaOptions} />
                  </Form.Item>

                  <Form.Item
                    label="Points Per Route"
                    name="points_per_route"
                    initialValue={15}
                  >
                    <InputNumber min={1} style={{ width: "100%" }} />
                  </Form.Item>

                  <Form.Item label="Remainder Handling">
                    <Select
                      value={remainderStrategy}
                      onChange={setRemainderStrategy}
                      options={[
                        { label: "Create Separate Route", value: "separate" },
                        { label: "Merge Into Last Route", value: "merge" },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item label="Optimization">
                    <Select
                      value={optimizationStrategy}
                      onChange={setOptimizationStrategy}
                      options={[
                        { label: "Nearest Neighbor", value: "nearest" },
                        { label: "Latitude Sort", value: "lat_lng" },
                      ]}
                    />
                  </Form.Item>

                  <Button
                    type="primary"
                    loading={previewLoading}
                    onClick={handlePreviewRoutes}
                    block
                  >
                    Preview Routes
                  </Button>
                </>
              )}

              {/* Manual Configuration Section */}
              {routeMode === "manual" && (
                <>
                  <Form.Item
                    label="Route Name"
                    name="name"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item label="Description" name="description">
                    <Input.TextArea rows={3} />
                  </Form.Item>

                  <Form.Item
                    label={
                      <Space>
                        Ordered Route
                        <Tooltip
                          title="On: staff must visit the points in the exact sequence set below. Off (Unordered): staff can check in to any point, in any order -- use this for routes with no fixed order, and it's the best fit for an assignment set to Flexible (No Fixed Schedule), where staff pick their own points and pace."
                          overlayStyle={{ maxWidth: 320 }}
                        >
                          <InfoCircleOutlined style={{ color: "#0e3e8b", cursor: "help" }} />
                        </Tooltip>
                      </Space>
                    }
                    name="is_ordered"
                    valuePropName="checked"
                    initialValue={true}
                  >
                    <Switch
                      // checked={orderedValue}
                      onChange={(checked) => {
                        if (selectedRoute?.active_execution_count > 0) {
                          Modal.confirm({
                            title: "Active Route Execution Detected",
                            content: (
                              <>
                                <p>
                                  This route is currently being executed by{" "}
                                  <b>{selectedRoute.active_execution_count}</b>{" "}
                                  employee(s).
                                </p>

                                <p>
                                  Changing route order will affect route
                                  execution immediately.
                                </p>

                                <p>
                                  Remaining route points may be visited in a
                                  different sequence.
                                </p>
                              </>
                            ),

                            okText: "Continue",
                            cancelText: "Cancel",

                            onOk() {
                              form.setFieldValue("is_ordered", checked);
                            },
                          });

                          return;
                        }

                        form.setFieldValue("is_ordered", checked);
                      }}
                    />
                  </Form.Item>

                  <Select
                    placeholder="Add Route Point"
                    style={{ width: "100%" }}
                    value={pointToAdd}
                    onChange={(value) => {
                      handleAddPoint(value);
                      setPointToAdd(undefined);
                    }}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={routePoints
                      .filter(
                        (point) =>
                          !selectedPoints.some(
                            (p) => p.route_point_id === point.id,
                          ),
                      )
                      .map((point) => ({
                        label: point.name,
                        value: point.id,
                      }))
                    }
                    disabled={selectedRoute?.active_execution_count > 0}
                  />
                </>
              )}
            </Form>

            {/* Manual Ordering Interactive List Section */}
            {routeMode === "manual" && (
              <div style={{ marginTop: 20 }}>
                {console.log("activecount: ", selectedRoute)}
                {Number(selectedRoute?.active_execution_count) > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`This route currently has ${selectedRoute.active_execution_count} active execution(s)`}
                    description={
                      <div>
                        <div style={{ marginBottom: 8 }}>
                          You may still update:
                        </div>

                        <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
                          <li>Route Name</li>
                          <li>Description</li>
                          <li>Ordered / Unordered setting</li>
                        </ul>

                        <div style={{ marginBottom: 8 }}>
                          The following cannot be changed until all active
                          executions are completed:
                        </div>

                        <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                          <li>Route Points</li>
                          <li>Route Sequence</li>
                          <li>Expected Duration</li>
                          <li>Mandatory Flags</li>
                        </ul>
                      </div>
                    }
                  />
                )}
                <Text strong>Route Point Sequence</Text>

                <DragDropContext
                  onDragEnd={
                    selectedRoute?.active_execution_count > 0
                      ? () => {}
                      : onDragEnd
                  }
                >
                  <Droppable droppableId="points">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {selectedPoints.map((point, index) => (
                          <Draggable
                            key={`${point.id}-${index}`}
                            draggableId={`${point.id}-${index}`}
                            index={index}
                          >
                            {(provided) => (
                              <Card
                                size="small"
                                style={{
                                  marginTop: 10,
                                  cursor:
                                    selectedRoute?.active_execution_count > 0
                                      ? "not-allowed"
                                      : "grab",
                                  opacity:
                                    selectedRoute?.active_execution_count > 0
                                      ? 0.8
                                      : 1,
                                }}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <Row gutter={8} align="middle">
                                  <Col xs={24} sm={10}>
                                    <Text style={{ wordBreak: "break-word" }}>
                                      {index + 1}. {point.name}
                                    </Text>
                                  </Col>

                                  <Col
                                    xs={14}
                                    sm={8}
                                    style={{
                                      marginTop: "4px",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <InputNumber
                                      min={1}
                                      value={point.expected_duration_minutes}
                                      onChange={(value) => {
                                        const updated = selectedPoints.map(
                                          (item, i) =>
                                            i === index
                                              ? {
                                                  ...item,
                                                  expected_duration_minutes:
                                                    value,
                                                }
                                              : item,
                                        );
                                        setSelectedPoints(updated);
                                      }}
                                      disabled={
                                        selectedRoute?.active_execution_count >
                                        0
                                      }
                                      addonAfter="min"
                                      style={{ width: "100%" }}
                                    />
                                  </Col>

                                  {/* <Col
                                    xs={6}
                                    sm={4}
                                    style={{ textAlign: "center" }}
                                  >
                                    <Switch
                                      checked={point.is_mandatory}
                                      onChange={(checked) => {
                                        const updated = selectedPoints.map(
                                          (item, i) =>
                                            i === index
                                              ? {
                                                  ...item,
                                                  is_mandatory: checked,
                                                }
                                              : item,
                                        );
                                        setSelectedPoints(updated);
                                      }}
                                      disabled={
                                        selectedRoute?.active_execution_count >
                                        0
                                      }
                                    />
                                  </Col> */}

                                  <Col
                                    xs={4}
                                    sm={2}
                                    style={{ textAlign: "right" }}
                                  >
                                    <Button
                                      danger
                                      size="small"
                                      onClick={() => {
                                        const updated = selectedPoints.filter(
                                          (_, i) => i !== index,
                                        );
                                        setSelectedPoints(updated);
                                      }}
                                      disabled={
                                        selectedRoute?.active_execution_count >
                                        0
                                      }
                                    >
                                      X
                                    </Button>
                                  </Col>
                                </Row>
                              </Card>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            )}
          </Col>

          {/* Manual Mode Map Visualization Column */}
          <Col
            xs={24}
            md={14}
            lg={16}
            style={{ display: routeMode === "auto" ? "none" : "block" }}
          >
            {isLoaded && (
              <GoogleMap
                mapContainerStyle={{
                  width: "100%",
                  height: "500px",
                  minHeight: "350px",
                }}
                center={defaultCenter}
                zoom={zoom}
              >
                {directions && <DirectionsRenderer directions={directions} />}

                {selectedPoints.map((point, index) => (
                  <Marker
                    key={`${point.id}-${index}`}
                    label={`${index + 1}`}
                    position={{
                      lat: Number(point.latitude),
                      lng: Number(point.longitude),
                    }}
                  />
                ))}
              </GoogleMap>
            )}
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default Routes;